import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@i18n/useI18n';
import { DashList, DataListWrap, Grid, GridItemCol1, GridItemCol2, GridItemTitle, GridItemTitleLeft, Img, ItemContent, LegendTag, LegendWrap, PickButton } from './Dashboard.styled';
import moment from 'moment';
import { useStore } from '@store/appStore';
import db from '@utils/db';
import StudentThumbnail from '@components/StudentThumbnail';
import Missing from '@assets/svg/match/missing.svg';
import Late from '@assets/svg/match/late.svg';
import Attending from '@assets/svg/match/attending.svg';
import S_Icon from "@assets/svg/s_icon.svg";
import Cross_Icon from "@assets/svg/match/missing.svg";
import PieGraph from './PieGraph';
import Modal from '@components/Buttons/Modal';
import { BtnRow, PopupContent, PopupWrapper, TopBar } from '../ApprovalDetails/ApprovalDetails.styled';
import fixedNumber from '@utils/fixedNumber';
import EmptyBox from '../EmptyBox';
import { AttendanceIcon, Tags, Tag } from '../RegistrationDetails/RegistrationDetails.styled';
import { AttendanceMap } from '@models/AttendanceType';
import RainbowLoader from '@components/Loaders/RainbowLoader';
import ColGraph from './ColGraph';
import { ShowabsenceOrAttendance } from '@utils/percentageTools';

interface IProps {
  handleClick: (username, id, displayname, type, sick?) => void,
  secretary?: boolean,
}

export const Dashboard = (props: IProps) => {
  const { setSchoolViewFilter, client, settings, callNameSetting, setSchoolLeaderView, handleNote, setSchoolViewDateRange, schoolViewDateRange, schoolViewTags, filterDate, schoolViewFilter, systemSettings } = useStore();
  const [open, setOpen] = useState(false);
  const openRef = useRef(null)
  const i18n = useI18n();
  const [registrationCount, setRegistrationCount] = useState(null)
  const [data, setData] = useState(null)
  const [registrations, setRegistrations] = useState(null)
  const [missingRegTeacher, setMissingRegTeacher] = useState(null)
  const [missingRegLoading, setMissingRegLoading] = useState(true)
  const [missingReg, setMissingReg] = useState(null)
  const [absencePrDate, setAbsencePrDate] = useState(null)
  const [absencePrDateLoading, setAbsencePrDateLoading] = useState(true)
  const [notes, setNotes] = useState(null)
  const [approvals, setApprovals] = useState(null)
  const [registrationCountLoading, setRegistrationCountLoading] = useState(true)
  const [registrationsLoading, setRegistrationsLoading] = useState(true)
  const [studentsLoading, setStudentsLoading] = useState(true)
  const [approvalsLoading, setApprovalsLoading] = useState(true)
  const [tags, setTags] = useState([])
  const [tagsLoading, setTagsLoading] = useState(true)
  const [notesLoading, setNotesLoading] = useState(true)
  const [sickEventsLoading, setSickEventsLoading] = useState(true)
  const [allTags, setAllTags] = useState<any>([]);
  const [text, setText] = useState('')
  const [id, setId] = useState(null)
  const [editItem, setEditItem] = useState(null)
  const [students, setStudents] = useState(null)
  const [sickEvents, setSickEvents] = useState(null)
  const icons = {
    late: <Late />,
    missing: <Missing />,
    attending: <Attending />,
  };

  let s = systemSettings?.find(s => s.APP_Setting_ID === 74)  
  let s2 = systemSettings?.find(s => s.APP_Setting_ID === 77)  
  let allowNotes = systemSettings?.find(s => s.APP_Setting_ID === 9)
  let freeTextNotes = systemSettings?.find(s => s.APP_Setting_ID === 42)
  let useMissingTeacherRegistrations = systemSettings?.find(s => s.APP_Setting_ID === 105)
  // Sick registrations are available if either sick setting (50 = student, 53 = note/report) is on.
  const sickEnabled = [50, 53].some(id => String(systemSettings?.find(s => s.APP_Setting_ID === id)?.value).toLowerCase() === "true")
  
  useEffect(() => {
    changeLanguage()
  }, [i18n.language])

  function getFromDate() {
    let fromDate;
    if (schoolViewFilter.quickfilter0 === 0) fromDate = moment();
    else if (schoolViewFilter.quickfilter0 === 1) fromDate = moment().subtract(1, 'day');
    else if (schoolViewFilter.quickfilter0 === 2) fromDate = moment().subtract(7, 'day');
    else if (schoolViewFilter.quickfilter0 === 3) fromDate = moment().subtract(14, 'day');
    else if (schoolViewFilter.quickfilter0 === 4) fromDate = moment().subtract(30, 'day');
    else if (schoolViewFilter.quickfilter0 === 5) fromDate = moment(filterDate.from);
    else fromDate = moment(schoolViewDateRange?.fromDate0 ?? schoolViewFilter.fromDate0);
    return fromDate
  }

  function getToDate() {
    let toDate;
    if (schoolViewFilter.quickfilter0 === 0) toDate = moment();
    else if (schoolViewFilter.quickfilter0 === 1) toDate = moment().subtract(1, 'day');
    else if (schoolViewFilter.quickfilter0 === 2) toDate = moment();
    else if (schoolViewFilter.quickfilter0 === 3) toDate = moment();
    else if (schoolViewFilter.quickfilter0 === 4) toDate = moment();
    else if (schoolViewFilter.quickfilter0 === 5) toDate = moment();
    else toDate = moment(schoolViewDateRange?.toDate0 ?? schoolViewFilter.toDate0);
    return toDate
  }

  const changeLanguage = () => {
    setRegistrationCountLoading(true)
    setTimeout(() => {
      setRegistrationCountLoading(false)
    }, 500)
  }

  // Bumped on every refetch so a slow response for an old date range can't overwrite a newer one.
  const reqId = useRef(0)

  useEffect(() => {
    // filterDate is restored (saved custom range / semester) after the dashboard first mounts;
    // wait for it and refetch when it changes, otherwise the data reflects the wrong period.
    if (!filterDate?.from) return
    reqId.current++
    getData()
    // getTeacherMissingReg()
    // getAbsencePrDate();
    getNotes()
    getMissingRegs()
    getApprovals()
    getRegistrations()
    getTodaysRegistrations()
    getStudents()
    getSickEvents()
  }, [schoolViewFilter.quickfilter0, schoolViewDateRange.fromDate0, schoolViewDateRange.toDate0, filterDate?.from, filterDate?.to])

  const handleMissingRegistrationClick = (displayname) => {
    setSchoolViewFilter({ ...schoolViewFilter, search10: displayname })
    setSchoolLeaderView(10)
  }

  const getMissingRegs = async () => {
    if (useMissingTeacherRegistrations?.value != "true") {
      setMissingRegLoading(false)
      return
    }

    let missingReg = await db.GetMissingRegistrationsByTeacher(14)
    setMissingReg(missingReg)
    // console.log(missingReg)
    setMissingRegLoading(false)
  }


  const getTodaysRegistrations = async () => {
    const my = reqId.current
    setRegistrationCountLoading(true)
    let fromDate = getFromDate().format('YYYY-MM-DD')
    let toDate = getToDate().format('YYYY-MM-DD')

    let t = await db.CountAbsenceTypes(fromDate, toDate)
    if (my !== reqId.current) return
    if (t?.length > 0) {
      let absent = t.find(c => c.type === 2)
      let late = t.find(c => c.type === 3)
      let present = t.find(c => c.type === 1)

      let count = { absent: absent ? absent.type_count : 0, late: late ? late.type_count : 0, present: present ? present.type_count : 0 }
      if (count.absent > 0 || count.late > 0 || count.present > 0) {
        setRegistrationCount(count)
      }
      else {
        setRegistrationCount(null)
      }
    }
    else {
      setRegistrationCount(null)
    }
    setRegistrationCountLoading(false)
  }

  // const getAbsencePrDate = async () => {
  //   setAbsencePrDateLoading(true)
  //   let absence = await db.GetAbsencePrDate(filterDate.from, filterDate.to)
    
  //   setAbsencePrDate(absence && absence.length > 0 ? absence : [])
  //   setAbsencePrDateLoading(false)
  // }

  // const getTeacherMissingReg = async () => {
  //   setMissingRegLoading(true)
    
  //   let timezone = systemSettings.find((s) => s.APP_Setting_ID === 7);
  //   let missingReg = await db.GetAllRegistrations(filterDate.from, filterDate.to, timezone.value, 30)
  //   missingReg = missingReg.filter(a => a.absence_type !== 1).sort((a, b) => {
  //     if (a.date < b.date) return 1
  //     else if (a.date > b.date) return -1
  //     else return 0
  //   }
  //   )
  //   setMissingRegTeacher(registrations && registrations.length > 0 ? registrations : [])
  //   setMissingRegLoading(false)
  // }

  const getRegistrations = async () => {
    const my = reqId.current
    setRegistrationsLoading(true)
    let fromDate = getFromDate().format('YYYY-MM-DD')
    let toDate = getToDate().format('YYYY-MM-DD')

    let timezone = systemSettings.find((s) => s.APP_Setting_ID === 7);
    let registrations = await db.GetAllRegistrations(filterDate.from, filterDate.to, timezone.value, 30)
    if (my !== reqId.current) return
    registrations = registrations.filter(a => a.absence_type !== 1).sort((a, b) => {
      if (a.date < b.date) return 1
      else if (a.date > b.date) return -1
      else return 0
    }
    )
    setRegistrations(registrations && registrations.length > 0 ? registrations : [])
    setRegistrationsLoading(false)
  }

  const getStudents = async () => {
    const my = reqId.current
    setStudentsLoading(true)
    let fromDate = getFromDate().format('YYYY-MM-DD')
    let toDate = getToDate().format('YYYY-MM-DD')

    let students = await db.GetStudents(fromDate, toDate)
    if (my !== reqId.current) return
    students = students.sort((a, b) => {
      if (a.percentage < b.percentage) return 1
      else if (a.percentage > b.percentage) return -1
      else return 0
    }
    )
    setStudents(students && students.length > 0 ? students : [])
    setStudentsLoading(false)
  }

  const getApprovals = async () => {
    setApprovalsLoading(true)
    let approvals = await db.GetAbsenceRequests4Approval(client.UserName)
    setApprovals(approvals && approvals.length > 0 ? approvals.filter(a => a.waiting) : [])
    setApprovalsLoading(false)
  }

  const getSickEvents = async () => {
    if (!sickEnabled) {
      setSickEventsLoading(false)
      return
    }
    setSickEventsLoading(true)
    let events = await db.GetSickEvent4AllStudents(0)
    let absenceIds = [...new Set(events.map((item) => item.absence_id))]
    let temp = []
    for (let i = 0; i < absenceIds.length; i++) {
      let tempAbsences = events.filter(a => a.absence_id === absenceIds[i])
      let dates = tempAbsences.map(a => new Date(a.date))
      let fromDate = moment(Math.min(...dates)).format("DD-MM-YYYY")
      let toDate = moment(Math.max(...dates)).format("DD-MM-YYYY")
      let dateText = fromDate
      if (toDate !== fromDate) dateText = fromDate + " - " + toDate
      let days = {}

      let t = []
      for (let y = 0; y < tempAbsences.length; y++) {
        if (tempAbsences[y].starttime && tempAbsences[y].endtime && tempAbsences[y].displayname && tempAbsences[y].coursedescription) {
          let starttime = tempAbsences[y].starttime?.hours.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          }) + ":" + tempAbsences[y].starttime?.minutes.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          })
          let endtime = tempAbsences[y].endtime?.hours.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          }) + ":" + tempAbsences[y].endtime?.minutes.toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          })

          if (!days[tempAbsences[y].date]) days[tempAbsences[y].date] = []

          days[tempAbsences[y].date].push({ displayname: tempAbsences[y].displayname + " " + tempAbsences[y].coursedescription, starttime: starttime, endtime: endtime, date: tempAbsences[y].date })
          t.push({ displayname: tempAbsences[y].displayname + " " + tempAbsences[y].coursedescription, starttime: starttime, endtime: endtime, date: tempAbsences[y].date })
        }
      }

      temp.push({ absenceId: tempAbsences[0].absence_id, date: dateText, text: tempAbsences[0].text, timeblocks: t, tags: ConvertTags(tempAbsences[0].tag_ids, i, "sickevent"), days: days, username: tempAbsences[0].username, displayname: tempAbsences[0].student_displayname, student_id: tempAbsences[0].student_id })
    }


    setSickEvents(temp)
    setSickEventsLoading(false)
  }

  const getNotes = async () => {
    const my = reqId.current
    setNotesLoading(true)
    let fromDate = getFromDate().format('YYYY-MM-DD')
    let toDate = getToDate().format('YYYY-MM-DD')

    let allTags = await db.getAllNote_Tags();
    setAllTags(allTags);
    let notes = await db.GetNotesForDash(fromDate, toDate)
    if (my !== reqId.current) return
    notes = notes.sort((a, b) => {
      if (a.create_timestamp > b.create_timestamp) return -1
      else if (a.create_timestamp < b.create_timestamp) return 1
      else return 0
    })
    setNotes(notes && notes.length > 0 ? notes : [])
    setNotesLoading(false)
  }

  const ConvertTags = (list: any, index: any, type: any) => {
    if (list === null || list.length < 0) return [];

    let ids = list.split(',');
    let tags = [];
    for (let i = 0; i < ids.length; i++) {
      let tempTag = allTags.find((t) => t.tag_id == ids[i]);
      if (tempTag) {
        tags.push(tempTag);
      }
      else if (ids[i] == 99) tags.push({ tag_id: 99, name: "Late", html_color: "FED425" })
    }

    return tags.map((t, i) => <Tag key={type + "_" + index + "_tag_" + i} bg={t.html_color} colors={t.html_color === "FED425"}>{i18n.t("tag-" + t.name)}</Tag>)
  };

  const getData = async () => {
    const my = reqId.current
    setTagsLoading(true)
    let fromDate = getFromDate().format('YYYY-MM-DD')
    let toDate = getToDate().format('YYYY-MM-DD')

    let data = await db.CountAbsenceTags(fromDate, toDate)
    let allTags = await db.getAllNote_Tags();
    if (my !== reqId.current) return

    let labels = []
    let count = []
    let colors = []
    let tags = []

    for (let i = 0; i < data.length; i++) {
      const tagCount = data[i];
      let tempTag = allTags.find((t) => t.tag_id == tagCount.tag_id);
      if (tempTag) {
        labels.push(i18n.t("tag-" + tempTag.name))
        colors.push("#" + tempTag.html_color)
        count.push(tagCount.tag_id_count)
        tags.push({ name: "tag-" + tempTag.name, color: "#" + tempTag.html_color })
      }
      else if (tagCount.tag_id == 99) {
        labels.push(i18n.t("tag-Late"))
        colors.push("#FED425")
        count.push(tagCount.tag_id_count)
        tags.push({ name: "tag-Late", color: "#FED425" })
      }
    }
    setTags(tags)

    let t = {
      labels: labels,
      datasets: [{
        label: ' ',
        data: count,
        backgroundColor: colors,
        hoverOffset: 4
      }]
    }
    setData(t)
    setTagsLoading(false)
  }

  const handleClose = () => {
    setText('')
    setId(null)
    openRef.current.handleClose()
  }

  const handleOpen = (id, item) => {
    setEditItem(item)
    setId(id)
    setOpen(true)
  }

  const handleRequest = async (approve: boolean) => {
    if (id) {
      if (!approve) {
        if (text !== "") {
          let request = await db.ApproveDenyRequest(id, client.UserName, approve, text)
        }
      }
      else {
        let request = await db.ApproveDenyRequest(id, client.UserName, approve, text)
      }
      handleClose()
      getApprovals()
    }
  }

  const getPeriod = (startDate, endDate) => {
    let start = moment(startDate)
    let end = moment(endDate)
    return (start.format('DD-MM-YYYY') + ' - ' + end.format('DD-MM-YYYY'))
  }

  const getDateText = () => {
    if (schoolViewFilter.quickfilter0 === 0) return i18n.t("registration-graph-1")
    else if (schoolViewFilter.quickfilter0 === 1) return i18n.t("registration-graph-0")
    else if (schoolViewFilter.quickfilter0 === 2) return i18n.t("registration-graph-2")
    else if (schoolViewFilter.quickfilter0 === 3) return i18n.t("registration-graph-3")
    else if (schoolViewFilter.quickfilter0 === 4) return i18n.t("registration-graph-4")
    else if (schoolViewFilter.quickfilter0 === 5) return i18n.t("registration-graph-5")
  }

  return (<>
    {open ? <Modal ref={openRef} onClose={setOpen} backColor='rgba(0,0,0,0.25)'>
      <PopupWrapper>
        <TopBar>
          <span>{i18n.t("approval-title")}</span>
          <button onClick={() => handleClose()}><Cross_Icon /></button>
        </TopBar>
        <PopupContent>
          <span>{i18n.t("header-name")}: <b>{editItem ? editItem.displayname : null}</b></span>
          <span>{i18n.t("period")}: <b>{editItem ? getPeriod(editItem.startdatetime, editItem.enddatetime) : null}</b></span>
          <span>{i18n.t("header-comment")}: <b>{editItem ? editItem.request_text : null}</b></span>
          <p>{i18n.t("approval-text1")}</p>
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder={i18n.t("approval-text2")} />
        </PopupContent>
        <BtnRow>
          <button onClick={() => handleRequest(true)}>{i18n.t("approve")}</button>
          <button disabled={text === ""} onClick={() => handleRequest(false)}>{i18n.t("reject")}</button>
          <button onClick={() => handleClose()}>{i18n.t("close")}</button>
        </BtnRow>
      </PopupWrapper>
    </Modal> : null}




    <Grid>
      <GridItemCol1>
        <GridItemTitle>{getDateText()} {/*<Tooltip content={"tool"}><Tooltip_Icon /></Tooltip>*/}</GridItemTitle>
        {registrationCountLoading ? <RainbowLoader /> : registrationCount ?
          <ItemContent>
            <ColGraph data={registrationCount} />
          </ItemContent>
          : <EmptyBox Graphic='graph' text={i18n.t("empty-no-graph")} />}

      </GridItemCol1>

      <GridItemCol1>
        <GridItemTitle>{i18n.t("dash-title2")}{/*<Tooltip content={"tool"}><Tooltip_Icon /></Tooltip>*/}</GridItemTitle>


        {tagsLoading ? <RainbowLoader /> : tags.length > 0 ? <ItemContent>
          <PieGraph data={data} />

          <LegendWrap>
            {tags ? tags.map((t, index) => <LegendTag key={"legend_" + index} color={t.color}>{i18n.t(t.name)} <div /><span>({data.datasets[0].data[index]})</span></LegendTag>) : null}
          </LegendWrap>
        </ItemContent> : <EmptyBox Graphic='graph' text={i18n.t("empty-no-graph")} />}
      </GridItemCol1>

      {/* <GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("dash-title8")}
         
        </GridItemTitleLeft>

        {registrationsLoading ? <RainbowLoader /> : registrations?.length > 0 ? <ItemContent>
            <MultiColData data={absencePrDate} />
          </ItemContent> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2> */}

      {allowNotes.value === "true"?<GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("notes")}
          {/*<Tooltip content={"tool"}><Tooltip_Icon /></Tooltip>*/}
          <button onClick={() => setSchoolLeaderView(4)}>{i18n.t("go-to-overview")}</button>
        </GridItemTitleLeft>

        {notesLoading ? <RainbowLoader /> : notes.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                {freeTextNotes.value.toLowerCase() !== "none"?<td>{i18n.t("header-note")}</td>:null}
                <td>{i18n.t("header-tags")}</td>
                <td>{i18n.t("header-created")}</td>
              </tr>
              {notes ? notes.slice(0, 7).map((n, index) =>
                <tr key={"note_" + index}>
                  <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={n?.displayname} src={n.username} /></Img></td>
                  <td className={"clickable"} onClick={() => props.handleClick(n.username, n.sis_id, n.displayname, "STUDENT", false)}>{n.displayname}</td>
                  {freeTextNotes.value.toLowerCase() !== "none"?<td className={"clickable"} onClick={() => handleNote(n.sis_id, n.displayname)} style={{wordBreak:"break-all"}}>{n.text}</td>:null}
                  <td className={"clickable"} onClick={() => handleNote(n.sis_id, n.displayname)}><Tags>{ConvertTags(n.tag_ids, index, "note")}</Tags></td>
                  <td><span>{moment(n.create_timestamp).format("DD-MM-YYYY")}</span></td>
                </tr>

              ) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox icon={<S_Icon />} text={i18n.t("empty-no-data1")} />}
      </GridItemCol2>:null}


      {/* <GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("header-missing-reg") + " " + i18n.t("teacher")  }
        </GridItemTitleLeft>

        {registrationsLoading ? <RainbowLoader /> : registrations?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td className={"auto"}>{i18n.t("header-name")}</td>
                <td className={"center"}>{i18n.t("Registreret")}</td>
                <td className={"center"}>{i18n.t("Ikke registreret")}</td>
              </tr>
              {registrations ? registrations.slice(0, 7).map((r, index) => <tr key={"misssing_registration_" + index}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={r?.student_displayname} src={r.username} /></Img></td>
                <td className={"auto"}>{r.student_displayname}</td>
                <td className={"center red"}>11</td>
                <td className={"center green"}>73</td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2> */}


      <GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("dash-title3")}
          <button onClick={() => setSchoolLeaderView(3)} >{i18n.t("go-to-overview")}</button>
        </GridItemTitleLeft>

        {registrationsLoading ? <RainbowLoader /> : registrations?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                <td>{i18n.t("date")}</td>
                <td className={"right"}>{i18n.t(settings.SHOWPRESENCE?"header-peried-attendance":"header-peried")}</td>
                <td className={"center"}>{i18n.t("header-reg")}</td>
              </tr>
              {registrations ? registrations.slice(0, 7).map((r, index) => <tr key={"registration_" + index}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={r?.student_displayname} src={r.username} /></Img></td>
                <td className={"clickable"} onClick={() => props.handleClick(r.username, r.student_id, r.student_displayname, "STUDENT", false)}>{r.student_displayname}</td>
                <td><span>{moment(r.date).format('DD-MM-YYYY')}</span></td>
                <td className={"right"}>{fixedNumber(ShowabsenceOrAttendance(settings.SHOWPRESENCE ,r.percentage))}%</td>
                <td className={"center"}><AttendanceIcon>{icons[AttendanceMap[r.absence_type]]}</AttendanceIcon></td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2>

      {useMissingTeacherRegistrations?.value == "true" ?<GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("dash-title10")}
          <button onClick={() => setSchoolLeaderView(10)} >{i18n.t("go-to-overview")}</button>
        </GridItemTitleLeft>
        {missingRegLoading ? <RainbowLoader /> : missingReg?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                <td className={"right"}>{i18n.t("missing-teacher-reg")}</td>
              </tr>
              {missingReg ? missingReg.slice(0, 7).map((r, index) => <tr className={"clickable"} key={"missingReg_" + index} onClick={() => handleMissingRegistrationClick(r.displayname)}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"0.9rem"} name={r?.displayname} src={r.username} /></Img></td>
                <td>{r.displayname}</td>
                <td className={"right"}>{r.missing_lessons}</td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2>:null}

      {((props.secretary && s2.value.toLowerCase() === "true") || (!props.secretary && s.value.toLowerCase() === "true"))?<GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("dash-title4")}
          <button onClick={() => setSchoolLeaderView(5)}>{i18n.t("go-to-overview")}</button>
        </GridItemTitleLeft>

        {approvalsLoading ? <RainbowLoader /> : approvals?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                <td>{i18n.t("header-comment")}</td>
                <td>{i18n.t("date")}</td>
                <td className={"right"}></td>
              </tr>
              {approvals ? approvals.slice(0, 7).map((a, index) => <tr key={"approval_" + index}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={a?.displayname} src={a.username} /></Img></td>
                <td className={"clickable"} onClick={() => props.handleClick(a.username, a.student_id, a.displayname, "STUDENT", false)}>{a.displayname}</td>
                <td>{a.request_text}</td>
                <td><span>{moment(a.createdatetimestamp).format('DD-MM-YYYY')}</span></td>
                <td className={"right"}><PickButton onClick={() => handleOpen(a.request_id, a)}>{i18n.t("pick-action")}</PickButton></td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='user' text={i18n.t("empty-no-data3")} />}
      </GridItemCol2>:null}

      <GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t(settings.SHOWPRESENCE?"dash-title5-attendance":"dash-title5")}
          <button onClick={() => setSchoolLeaderView(1)} >{i18n.t("go-to-overview")}</button>

        </GridItemTitleLeft>

        {studentsLoading ? <RainbowLoader /> : students?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                <td className={"right"}>{i18n.t(settings.SHOWPRESENCE?"header-peried-total-attendance":"header-peried-total")}</td>
              </tr>
              {students ? students.slice(0, 7).map((r, index) => <tr key={"student_" + index}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={r?.displayname} src={r.username} /></Img></td>
                <td className={"clickable"} onClick={() => props.handleClick(r.username, r.student_id, r.displayname, "STUDENT", false)}>{r.displayname}</td>
                <td className={"right"}>{fixedNumber(ShowabsenceOrAttendance(settings.SHOWPRESENCE , r.percentage))}%</td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2>

      {sickEnabled ? <GridItemCol2>
        <GridItemTitleLeft>
          {i18n.t("dash-title6")}
          {//<button onClick={() => setSchoolLeaderView(1)} >Gå til oversigt</button>
          }
        </GridItemTitleLeft>

        {sickEventsLoading ? <RainbowLoader /> : sickEvents?.length > 0 ? <DataListWrap>
          <DashList>
            <tbody>
              <tr>
                <td className={"imgWidth"}></td>
                <td>{i18n.t("header-name")}</td>
                <td>{i18n.t("date")}</td>
              </tr>
              {sickEvents ? sickEvents.slice(0, 7).map((r, index) => <tr key={"sickevent_" + index}>
                <td className={"imgWidth"}><Img><StudentThumbnail font={"1em"} name={r?.displayname} src={r.username} /></Img></td>
                <td className={"clickable"} onClick={() => props.handleClick(r.username, r.student_id, r.displayname, "STUDENT", true)}>{r.displayname}</td>
                <td>{r.date}</td>
              </tr>) : null}
            </tbody>
          </DashList>
        </DataListWrap> : <EmptyBox Graphic='users' text={i18n.t("empty-no-data2")} />}
      </GridItemCol2> : null}
    </Grid>
  </>);
};

export default Dashboard;



